import { useQuery } from '@tanstack/react-query'
import { topliApi } from '@/shared/api/topli/TopliApi'
import { ResidentialsByPageParams, ResidentialsRequestParams } from '../model/types'

export const residentialsQueryKey = 'residentials'
export const currentResidentialByIdQueryKey = 'currentResidentialById'
export const residentialsByPageQueryKey = 'residentialsByPage'
export const residentialAmenitiesByPageQueryKey = 'residentialAmenitiesByPage'
export const searchResidentialsQueryKey = 'searchBuildings'

export const fetchResidentials = async (language: string) => {
  const response = await topliApi.residential.list({ language })
  return response
}

export const fetchResidentialsByPage = async (params: ResidentialsByPageParams) => {
  const {
    sorting,
    limit,
    offset,
    search = '',
    language,
    projectsId,
    checked_manual,
    grades,
    activity,
    international_homepage,
    local_homepage,
    searchById,
  } = params

  const requestParams: ResidentialsRequestParams = {
    limit,
    offset,
    sort: sorting || null,
    language,
  }

  if (search?.length) {
    requestParams.translations__name__icontains = search
    requestParams.translations__language__icontains = language
  }

  if (projectsId?.length) {
    requestParams.id__in = projectsId
  }

  if (checked_manual !== undefined) {
    requestParams.checked_manual = checked_manual
  }

  if (grades?.length) {
    requestParams.grade__in = grades
  }

  if (activity !== undefined) {
    requestParams.activity = activity
  }

  if (international_homepage !== undefined) {
    requestParams.international_homepage = international_homepage
  }

  if (local_homepage !== undefined) {
    requestParams.local_homepage = local_homepage
  }

  if (searchById?.length) {
    requestParams.id__icontains = searchById
  }

  const response = await topliApi.residential.list(requestParams)
  return response
}

export const fetchResidentialById = async (id?: number) => {
  if (!id) return
  const response = await topliApi.residential.get(id)
  return response
}

export const searchResidentialsByText = async (language: string, search?: string) => {
  if (!search) return
  const response = await topliApi.residential.list({
    translations__name__icontains: search,
    translations__language__icontains: language,
    language: language,
  })
  return response
}

export const fetchResidentialAmenitiesByPage = async (
  limit: number,
  offset: number,
  language: string,
) => {
  const response = await topliApi.residentialAmenity.list({ limit, offset, language })
  return response
}

export const useSearchResidentialsByText = (language: string, search?: string) => {
  const data = useQuery({
    queryKey: [searchResidentialsQueryKey, search, language],
    queryFn: () => searchResidentialsByText(language, search),
    enabled: !!search,
  })
  return data
}

export const useResidentials = (language: string) =>
  useQuery({
    queryKey: [residentialsQueryKey, language],
    queryFn: () => fetchResidentials(language),
  })

export const useResidentialsByPage = (params: ResidentialsByPageParams) =>
  useQuery({
    queryKey: [
      residentialsByPageQueryKey,
      params.sorting,
      params.offset,
      params.limit,
      params.search || '',
      params.language,
      params.projectsId,
      params.checked_manual,
      params.grades,
      params.activity,
      params.international_homepage,
      params.local_homepage,
      params.searchById,
    ],
    queryFn: () => fetchResidentialsByPage(params),
  })

export const useResidentialById = (id?: number, language?: string) =>
  useQuery({
    queryKey: [currentResidentialByIdQueryKey, id, language],
    queryFn: () => fetchResidentialById(id),
    enabled: !!id,
  })

export const useResidentialAmenitiesByPage = (limit: number, page: number, language: string) =>
  useQuery({
    queryKey: [residentialAmenitiesByPageQueryKey, page, language],
    queryFn: () => fetchResidentialAmenitiesByPage(limit, page, language),
  })
